import { jwtDecode } from 'jwt-decode';
import { createContext, useContext, useState, useEffect } from 'react';

import { useUnidadeDAF } from '../../services/enum/useUnidadeDaf';
import { getUserData } from '../../services/userService';
import { getUser } from '../contexts/Auth/RequireAuth';
import { User } from '../types/User';

// Contexto unificado
interface UserContextType {
  nomeCompleto: string;
  unidadeCadastro: string;
  papel: string;
  isLoading: boolean;
  isAdmin: boolean;
  hasTpVinculo: boolean;
  setUserData: (data: {
    nomeCompleto: string;
    unidadeCadastro: string;
  }) => void;
}

const UserContext = createContext<UserContextType | null>(null);

// Provider unificado
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userData, setUserData] = useState({
    nomeCompleto: '',
    unidadeCadastro: '',
  });
  const [papel, setPapel] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const unidadeDAF = useUnidadeDAF();

  const isAdmin = (): boolean => {
    const user = getUser();
    if (!user?.papel || !Array.isArray(user.papel)) return false;
    return user.papel.some(
      (p: any) => p.papelUsuario === 1 || p.papelUsuario === 2,
    );
  };

  const hasTpVinculo = (): boolean => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const decoded: any = jwtDecode(token);
      return decoded.tpVinculo === '1' || decoded.tpVinculo === 1;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      try {
        const user = getUser();
        if (user?.sub) {
          const userData = await getUserData(user.sub);
          if (isMounted) {
            setUserData({
              nomeCompleto: userData.nomeCompleto || 'Usuário',
              unidadeCadastro:
                unidadeDAF.find(
                  (u) => u.value === userData.unidadeCadastro?.toString(),
                )?.label || '',
            });
            setPapel(userData.papel || '');
          }
        }
      } catch {
        if (isMounted)
          setUserData({ nomeCompleto: 'Usuário', unidadeCadastro: '' });
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchUserData();
    return () => {
      isMounted = false;
    };
  }, [unidadeDAF]);

  const updateUserData = (data: {
    nomeCompleto: string;
    unidadeCadastro: string;
  }) => {
    setUserData(data);
  };

  const value = {
    ...userData,
    papel,
    isLoading,
    isAdmin: isAdmin(),
    hasTpVinculo: hasTpVinculo(),
    setUserData: updateUserData,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};
